import api from './api'

/**
 * Payment Service
 * Handles all payment-related API calls
 */
class PaymentService {
  /**
   * Get list of payments with filtering
   * GET v1/{club}/payments
   */
  async getPayments(clubUuid: string, params?: any): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/payments`, { params })
    return response.data
  }

  /**
   * Create new payment (single or recurring)
   * POST v1/{club}/payments
   */
  async createPayment(clubUuid: string, data: any): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/payments`, data)
    return response.data
  }

  /**
   * Delete a single payment
   * DELETE v1/{club}/payments/{payment}
   */
  async deletePayment(clubUuid: string, paymentUuid: string): Promise<any> {
    const response = await api.delete(`v1/${clubUuid}/payments/${paymentUuid}`)
    return response.data
  }

  /**
   * Mark a single payment as paid
   * PATCH v1/{club}/payments/{payment}/mark-as-paid
   */
  async markAsPaid(clubUuid: string, paymentUuid: string): Promise<any> {
    const response = await api.patch(`v1/${clubUuid}/payments/${paymentUuid}/mark-as-paid`)
    return response.data
  }

  /**
   * Mark multiple payments as paid (bulk operation)
   * POST v1/{club}/payments/bulk-mark-as-paid
   */
  async bulkMarkAsPaid(clubUuid: string, paymentUuids: string[]): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/payments/bulk-mark-as-paid`, {
      payment_uuids: paymentUuids
    })
    return response.data
  }

  /**
   * Delete multiple payments (bulk operation)
   * POST v1/{club}/payments/bulk-delete
   */
  async bulkDelete(clubUuid: string, paymentUuids: string[]): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/payments/bulk-delete`, {
      payment_uuids: paymentUuids
    })
    return response.data
  }

  /**
   * Get single payment details
   * GET v1/{club}/payments/{payment}
   */
  async getPayment(clubUuid: string, paymentUuid: string): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/payments/${paymentUuid}`)
    return response.data
  }

  /**
   * Update a single payment
   * PUT v1/{club}/payments/{payment}
   */
  async updatePayment(clubUuid: string, paymentUuid: string, data: any): Promise<any> {
    const response = await api.put(`v1/${clubUuid}/payments/${paymentUuid}`, data)
    return response.data
  }

  /**
   * Get list of recurring payments
   * GET v1/{club}/recurring-payments
   */
  async getRecurringPayments(clubUuid: string, params?: any): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/recurring-payments`, { params })
    return response.data
  }

  /**
   * Delete a recurring payment
   * DELETE v1/{club}/recurring-payments/{recurringPayment}
   */
  async deleteRecurringPayment(clubUuid: string, recurringPaymentUuid: string): Promise<any> {
    const response = await api.delete(`v1/${clubUuid}/recurring-payments/${recurringPaymentUuid}`)
    return response.data
  }

  /**
   * Get single recurring payment details
   * GET v1/{club}/recurring-payments/{recurringPayment}
   */
  async getRecurringPayment(clubUuid: string, recurringPaymentUuid: string): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/recurring-payments/${recurringPaymentUuid}`)
    return response.data
  }

  /**
   * Update a recurring payment
   * PUT v1/{club}/recurring-payments/{recurringPayment}
   */
  async updateRecurringPayment(clubUuid: string, recurringPaymentUuid: string, data: any): Promise<any> {
    const response = await api.put(`v1/${clubUuid}/recurring-payments/${recurringPaymentUuid}`, data)
    return response.data
  }

  /**
   * Get public payment details by UUID and hash (no authentication required)
   * GET v1/public/payments/{uuid}/{hash}
   */
  async getPublicPayment(uuid: string, hash: string): Promise<any> {
    const response = await api.get(`v1/public/payments/${uuid}/${hash}`)
    return response.data
  }

  /**
   * Send payment reminder to selected payments
   * POST v1/{club}/payments/send-reminder
   */
  async sendReminder(clubUuid: string, paymentUuids: string[], message: string, sendEmail: boolean): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/payments/send-reminder`, {
      payment_uuids: paymentUuids,
      message,
      send_email: sendEmail
    })
    return response.data
  }

  /**
   * Get financial statistics
   * GET v1/{club}/financial-stats
   */
  async getFinancialStats(clubUuid: string, dateFrom: string, dateTo: string, paymentName?: string): Promise<any> {
    const params: Record<string, string> = { date_from: dateFrom, date_to: dateTo }
    if (paymentName) {
      params.payment_name = paymentName
    }
    const response = await api.get(`v1/${clubUuid}/financial-stats`, { params })
    return response.data
  }
}

export default new PaymentService()
